/**
 * useGeometrySelection Hook
 *
 * Provides single-click (LPM) selection handling for geometry layers in Selection mode.
 * Selects geometry without activating drawing tools.
 *
 * Features:
 * - Handles click event on geometry interactive layers
 * - ONLY active when activeTool === GeometryType.Selection
 * - Selects geometry via Zustand (selectGeometry action)
 * - Does NOT activate drawing tool (key difference from edit mode)
 * - Works with MapLibre GL JS event system
 *
 * Usage:
 * - Line/Shape: Pass interactiveLayerId (hitbox layer)
 * - Marker: Use direct HTML event listeners (not this hook)
 *
 * @module hooks/useGeometrySelection
 * @example
 * ```typescript
 * // In LineRenderer or ShapeRenderer:
 * useGeometrySelection({
 *   geometryId: geometry.id,
 *   interactiveLayerId: `line-hitbox-${geometry.id}`,
 * });
 * ```
 */

import { useCallback, useEffect } from 'react';
import { useMap } from '@vis.gl/react-maplibre';
import { useGeoMarkupStore } from '../store';
import { GeometryType } from '../types/geometry';
import type { MapLayerMouseEvent } from 'maplibre-gl';

/**
 * Options for useGeometrySelection hook
 */
export interface UseGeometrySelectionOptions {
  /** ID of the geometry */
  geometryId: string;
  /** ID of the interactive layer (hitbox layer) */
  interactiveLayerId: string;
}

/**
 * Hook for handling geometry single-click selection (Selection mode)
 *
 * Registers click event listener on geometry's interactive layer.
 * Selects geometry when clicked in Selection mode (does NOT activate drawing tool).
 *
 * @param options - Hook configuration options
 */
export const useGeometrySelection = ({
  geometryId,
  interactiveLayerId,
}: UseGeometrySelectionOptions) => {
  const { current: map } = useMap();

  // Zustand state (atomic selector)
  const activeTool = useGeoMarkupStore((s) => s.activeTool);
  const isTaskFormOpen = useGeoMarkupStore((s) => s.tasks.formIsOpen);

  // Zustand action (stable reference)
  const selectGeometry = useGeoMarkupStore((s) => s.selectGeometry);

  /**
   * Handle click on geometry (Selection mode only)
   *
   * Selects geometry WITHOUT activating drawing tool.
   * Only active when activeTool === GeometryType.Selection.
   */
  const handleClick = useCallback(
    (e: MapLayerMouseEvent) => {
      // Only handle click in Selection mode and when form is open
      if (activeTool === GeometryType.Selection && isTaskFormOpen) {
        e.preventDefault();
        e.originalEvent.stopPropagation();

        // Select geometry with screen and geographic positions (for delete button positioning)
        selectGeometry(
          geometryId,
          {
            x: e.originalEvent.clientX,
            y: e.originalEvent.clientY,
          },
          {
            lng: e.lngLat.lng,
            lat: e.lngLat.lat,
          }
        );
      }
    },
    [activeTool, isTaskFormOpen, geometryId, selectGeometry]
  );

  /**
   * Setup click event listener on interactive layer
   *
   * Only registers when in Selection mode for performance
   */
  useEffect(() => {
    if (!map || activeTool !== GeometryType.Selection) return;

    // Register click event on interactive hitbox layer
    map.on('click', interactiveLayerId, handleClick);

    return () => {
      map.off('click', interactiveLayerId, handleClick);
    };
  }, [map, interactiveLayerId, activeTool, handleClick]);

  return {
    /** Handler for manual usage if needed */
    handleClick,
  };
};
